import { useMemo, type Ref } from "react";

import type { WatchMovingAverageDraft } from "@/domains/watches/hooks/useWatchAuthoringState";
import { getAuthorableWatchSignals, type WatchSignalCatalog } from "../../lib/watchSignalCatalog";
import {
  describeMovingAverageCondition,
  getReviewedSignalPresenter,
  type MovingAverageDirection,
  type MovingAverageTriggerMode,
  type MovingAverageType,
  type MovingAverageWindow,
  type MovingAverageWindowPair,
} from "../../lib/watchSignalReleaseRegistry";
import styles from "./WatchMovingAverageFields.module.css";

export interface WatchMovingAverageFieldsProps {
  /** Progressive sub-step rendered by the Watch wizard. */
  mode: "setup" | "alert";
  /** Runtime-decoded catalog; only exact reviewed available leaves may produce controls. */
  catalog: WatchSignalCatalog | null;
  /** Explicit customer choices. Nothing is defaulted. */
  draft: WatchMovingAverageDraft;
  /** Confirmed or visible ticker used only for plain-language preview copy. */
  ticker: string;
  /** ID of the shared visible validation message. */
  errorMessageId: string;
  /** Validation state for each progressively disclosed choice. */
  invalidAverageType?: boolean;
  invalidWindow?: boolean;
  invalidWindowPair?: boolean;
  invalidTriggerMode?: boolean;
  invalidDirection?: boolean;
  /** Controlled mutations owned by the authoring reducer. */
  onAverageTypeChange: (averageType: MovingAverageType) => void;
  onWindowChange: (window: MovingAverageWindow) => void;
  onWindowPairChange: (windowPair: MovingAverageWindowPair) => void;
  onTriggerModeChange: (triggerMode: MovingAverageTriggerMode) => void;
  onDirectionChange: (direction: MovingAverageDirection) => void;
  /** Focus targets used when a transition fails validation. */
  averageTypeRef?: Ref<HTMLFieldSetElement>;
  windowRef?: Ref<HTMLFieldSetElement>;
  windowPairRef?: Ref<HTMLFieldSetElement>;
  triggerModeRef?: Ref<HTMLFieldSetElement>;
  directionRef?: Ref<HTMLFieldSetElement>;
  /** Optional style extension applied to the root. */
  className?: string;
}

const WINDOWS: readonly MovingAverageWindow[] = ["20", "50", "200"];
const WINDOW_PAIRS: ReadonlyArray<{
  value: MovingAverageWindowPair;
  label: string;
  description: string;
}> = [
  { value: "20_50", label: "20-day and 50-day", description: "Shorter- and medium-term trend." },
  { value: "20_200", label: "20-day and 200-day", description: "Shorter- and long-term trend." },
  { value: "50_200", label: "50-day and 200-day", description: "Common medium- and long-term crossover." },
];

/** Plain-language, completed-daily Moving averages setup and alert controls. */
export default function WatchMovingAverageFields({
  mode,
  catalog,
  draft,
  ticker,
  errorMessageId,
  invalidAverageType = false,
  invalidWindow = false,
  invalidWindowPair = false,
  invalidTriggerMode = false,
  invalidDirection = false,
  onAverageTypeChange,
  onWindowChange,
  onWindowPairChange,
  onTriggerModeChange,
  onDirectionChange,
  averageTypeRef,
  windowRef,
  windowPairRef,
  triggerModeRef,
  directionRef,
  className,
}: WatchMovingAverageFieldsProps) {
  const presenters = useMemo(() => getAuthorableWatchSignals(catalog, {
    job: "MARKET",
    subjectScope: "UNDERLYING",
  }).map(getReviewedSignalPresenter).filter((presenter) => (
    presenter?.kind === "MOVING_AVERAGE_RELATIONSHIP"
      || presenter?.kind === "MOVING_AVERAGE_CROSSOVER"
  )), [catalog]);
  const availableTypes = (["SMA", "EMA"] as const).filter((averageType) => (
    presenters.some((presenter) => presenter?.averageType === averageType
      && (draft.comparison === "PRICE_TO_AVERAGE"
        ? presenter.kind === "MOVING_AVERAGE_RELATIONSHIP"
        : presenter.kind === "MOVING_AVERAGE_CROSSOVER"))
  ));
  const availableModes = draft.averageType && draft.comparison === "PRICE_TO_AVERAGE"
    ? (["STATE", "CROSSING"] as const).filter((triggerMode) => presenters.some(
      (presenter) => presenter?.kind === "MOVING_AVERAGE_RELATIONSHIP"
        && presenter.averageType === draft.averageType
        && presenter.triggerMode === triggerMode,
    ))
    : [];

  if (mode === "setup") {
    return (
      <div className={`${styles.root} ${className ?? ""}`}>
        <p className={styles.context}>
          These Watches use the completed regular-session close. They never use today’s live, incomplete daily bar.
        </p>
        <fieldset
          ref={averageTypeRef}
          className={styles.choiceGroup}
          aria-invalid={invalidAverageType || undefined}
          aria-describedby={invalidAverageType ? errorMessageId : undefined}
        >
          <legend>Average type</legend>
          {availableTypes.includes("SMA") ? (
            <label className={draft.averageType === "SMA" ? styles.choiceSelected : styles.choice}>
              <input
                type="radio"
                name="moving-average-type"
                checked={draft.averageType === "SMA"}
                onChange={() => onAverageTypeChange("SMA")}
              />
              <span><strong>Simple moving average (SMA)</strong><small>Gives equal weight to each completed day.</small></span>
            </label>
          ) : null}
          {availableTypes.includes("EMA") ? (
            <label className={draft.averageType === "EMA" ? styles.choiceSelected : styles.choice}>
              <input
                type="radio"
                name="moving-average-type"
                checked={draft.averageType === "EMA"}
                onChange={() => onAverageTypeChange("EMA")}
              />
              <span><strong>Exponential moving average (EMA)</strong><small>Gives more weight to recent completed days.</small></span>
            </label>
          ) : null}
        </fieldset>

        {draft.averageType && draft.comparison === "PRICE_TO_AVERAGE" ? (
          <fieldset
            ref={windowRef}
            className={styles.compactGroup}
            aria-invalid={invalidWindow || undefined}
            aria-describedby={invalidWindow ? errorMessageId : undefined}
          >
            <legend>Number of completed days</legend>
            {WINDOWS.map((window) => (
              <label className={draft.window === window ? styles.pillSelected : styles.pill} key={window}>
                <input
                  type="radio"
                  name="moving-average-window"
                  checked={draft.window === window}
                  onChange={() => onWindowChange(window)}
                />
                {window} days
              </label>
            ))}
          </fieldset>
        ) : null}

        {draft.averageType && draft.comparison === "AVERAGE_CROSSOVER" ? (
          <fieldset
            ref={windowPairRef}
            className={styles.choiceGroup}
            aria-invalid={invalidWindowPair || undefined}
            aria-describedby={invalidWindowPair ? errorMessageId : undefined}
          >
            <legend>Faster and slower averages</legend>
            {WINDOW_PAIRS.map((pair) => (
              <label className={draft.windowPair === pair.value ? styles.choiceSelected : styles.choice} key={pair.value}>
                <input
                  type="radio"
                  name="moving-average-window-pair"
                  checked={draft.windowPair === pair.value}
                  onChange={() => onWindowPairChange(pair.value)}
                />
                <span><strong>{pair.label}</strong><small>{pair.description}</small></span>
              </label>
            ))}
          </fieldset>
        ) : null}
      </div>
    );
  }

  return (
    <div className={`${styles.root} ${className ?? ""}`}>
      {draft.comparison === "PRICE_TO_AVERAGE" ? (
        <fieldset
          ref={triggerModeRef}
          className={styles.choiceGroup}
          aria-invalid={invalidTriggerMode || undefined}
          aria-describedby={invalidTriggerMode ? errorMessageId : undefined}
        >
          <legend>When should we alert?</legend>
          {availableModes.includes("STATE") ? (
            <label className={draft.triggerMode === "STATE" ? styles.choiceSelected : styles.choice}>
              <input
                type="radio"
                name="moving-average-trigger-mode"
                checked={draft.triggerMode === "STATE"}
                onChange={() => onTriggerModeChange("STATE")}
              />
              <span><strong>When it is on that side</strong><small>The first fresh completed-day check may alert if the condition is already true.</small></span>
            </label>
          ) : null}
          {availableModes.includes("CROSSING") ? (
            <label className={draft.triggerMode === "CROSSING" ? styles.choiceSelected : styles.choice}>
              <input
                type="radio"
                name="moving-average-trigger-mode"
                checked={draft.triggerMode === "CROSSING"}
                onChange={() => onTriggerModeChange("CROSSING")}
              />
              <span><strong>Only after it crosses</strong><small>The first fresh check establishes a baseline. Only a later completed-day crossing can alert.</small></span>
            </label>
          ) : null}
        </fieldset>
      ) : (
        <p className={styles.context}>
          The first fresh completed-day check establishes a baseline. Only a later crossover can alert.
        </p>
      )}

      {(draft.comparison === "AVERAGE_CROSSOVER" || draft.triggerMode) ? (
        <fieldset
          ref={directionRef}
          className={styles.compactGroup}
          aria-invalid={invalidDirection || undefined}
          aria-describedby={invalidDirection ? errorMessageId : undefined}
        >
          <legend>{draft.comparison === "PRICE_TO_AVERAGE" && draft.triggerMode === "STATE"
            ? "Completed daily close"
            : "Crossing direction"}</legend>
          <label className={draft.direction === "ABOVE" ? styles.pillSelected : styles.pill}>
            <input
              type="radio"
              name="moving-average-direction"
              checked={draft.direction === "ABOVE"}
              onChange={() => onDirectionChange("ABOVE")}
            />
            {draft.comparison === "PRICE_TO_AVERAGE" && draft.triggerMode === "STATE" ? "Is above" : "Crosses above"}
          </label>
          <label className={draft.direction === "BELOW" ? styles.pillSelected : styles.pill}>
            <input
              type="radio"
              name="moving-average-direction"
              checked={draft.direction === "BELOW"}
              onChange={() => onDirectionChange("BELOW")}
            />
            {draft.comparison === "PRICE_TO_AVERAGE" && draft.triggerMode === "STATE" ? "Is below" : "Crosses below"}
          </label>
        </fieldset>
      ) : null}

      <p className={styles.rulePreview}>
        <strong>Alert rule</strong>
        {describeMovingAverageCondition({ ticker, ...draft })}
      </p>
    </div>
  );
}
